import { memo, useMemo } from 'react'
import { FileChip } from '@/components/file-chip/FileChip'
import PageCitation from '@/medical-common/components/PageCitation'
import type { CellPosition, ScrollToCell } from '@/medical-common/hooks/use-table-cell-scroll'
import { useAppSelector } from '@/store/store-hooks'
import { RootState } from '@/store/store'
import { useMedSummaryParent } from '@/medical-billing-summaries/parent-context'
import { selectAssetFileByUserFileId } from '@/medical-billing-summaries/asset/store/selectors'
import type { MedSummarySourceRef } from '@/medical-billing-summaries/sources/schemas'
import { useOpenMedSummarySource } from '@/medical-billing-summaries/sources/useOpenMedSummarySource'

interface MedSummarySourceFileCellProps {
  source: MedSummarySourceRef
  pages: number[]
  rowId: string
  cellId: string
  scrollToCell: ScrollToCell
}

/**
 * Med Summary Source File Cell
 *
 * Table cell component for displaying a source file chip and page chips side by side.
 */
export default memo(function MedSummarySourceFileCell(props: MedSummarySourceFileCellProps) {
  const { source, pages, rowId, cellId, scrollToCell } = props
  const { id: parentId } = useMedSummaryParent()
  const { openMedSummarySource, sourceLoadError } = useOpenMedSummarySource()

  // Non-PDF sources open their stored conversion (AI-94) so page numbers and highlight
  // geometry line up with what was extracted.
  const hasConvertedPdf = useAppSelector(
    (state: RootState) => selectAssetFileByUserFileId(state, { assetId: parentId, userFileId: source.source_id })?.converted_pdf_path != null
  )

  // Stable cellPosition object (avoids new reference on every render)
  const cellPosition: CellPosition = useMemo(() => ({ rowId, cellId }), [rowId, cellId])

  const sourceError = sourceLoadError?.sourceId === source.source_id ? sourceLoadError.message : null

  // Open the source file with no highlights (optionally on specific page)
  const openFile = async (pageNumber?: number) => {
    scrollToCell(cellPosition)
    await openMedSummarySource(source, {
      startingPage: pageNumber,
      convertedPdf: hasConvertedPdf ? { assetId: parentId, userFileId: source.source_id } : undefined,
    })
  }

  return (
    <div className="flex items-start">
      {/* File chip section */}
      <div className="w-[297px] p-2">
        <FileChip name={source.display_name} onClick={() => openFile()} status={sourceError ? 'error' : undefined} errorMessage={sourceError ?? undefined} />
      </div>

      {/* Page chips section - extends horizontally without wrapping */}
      {pages.length > 0 && (
        <div className="px-3.5 pt-2.5 pb-2 flex gap-[6px] items-start">
          {pages.map((page: number, index: number) => (
            <PageCitation key={`page-${page}-${index}`} pages={[page]} onClick={() => openFile(page)} />
          ))}
        </div>
      )}
    </div>
  )
})
